import React, { useRef, useState } from 'react';
import { Paperclip, Send, X } from 'lucide-react';

async function prepareImage(file) {
  if (!['image/png', 'image/jpeg'].includes(file.type)) throw new Error('Please choose PNG or JPG screenshots.');
  if (file.size > 10 * 1024 * 1024) throw new Error('Each original screenshot must be under 10 MB.');
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = url; });
    const scale = Math.min(1, 2048 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.9, 0.75, 0.55]) {
      const data = canvas.toDataURL('image/jpeg', quality);
      if (data.length < 1350000) return data;
    }
    throw new Error('Screenshot is too large. Please crop it and try again.');
  } finally { URL.revokeObjectURL(url); }
}

export function AssistantAttachments({ images = [] }) {
  const [preview, setPreview] = useState(null);
  return <>
    <div className="flex flex-wrap gap-2 mt-2">
      {images.filter((s) => typeof s === 'string' && /^data:image\/(png|jpeg);base64,/.test(s)).map((src, i) =>
        <button type="button" key={i} onClick={() => setPreview(src)} aria-label={`View screenshot ${i + 1}`}>
          <img src={src} alt={`Screenshot ${i + 1}`} className="h-24 max-w-40 rounded-lg border border-border object-contain" />
        </button>)}
    </div>
    {preview && <div role="dialog" aria-modal="true" aria-label="Screenshot preview" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-8" onClick={() => setPreview(null)} onKeyDown={(e) => { if (e.key === 'Escape') setPreview(null); }}>
      <button autoFocus type="button" aria-label="Close screenshot" className="absolute top-4 right-4 text-white" onClick={() => setPreview(null)}><X /></button>
      <img src={preview} alt="Screenshot enlarged" className="max-h-full max-w-full object-contain" />
    </div>}
  </>;
}

export default function AssistantComposer({ value, onChange, images, onImagesChange, onSend, disabled, busy, inputRef, testId = 'assistant-input' }) {
  const fileRef = useRef(null);
  const processingRef = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const locked = disabled || busy || processing;
  const add = async (files) => {
    if (disabled || busy || processingRef.current) return;
    if (images.length + files.length > 3) { setError('Attach up to 3 screenshots per message.'); return; }
    processingRef.current = true; setProcessing(true); setError('');
    try { const added = await Promise.all(files.map(prepareImage)); onImagesChange((cur) => [...cur, ...added].slice(0, 3)); }
    catch (e) { setError(e.message || 'Could not read that screenshot.'); }
    finally { processingRef.current = false; setProcessing(false); }
  };
  const submit = (e) => { e?.preventDefault(); if (!locked && (value.trim() || images.length)) onSend(); };
  return <form onSubmit={submit} className="border-t border-border p-3"
    onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
    onDrop={(e) => { e.preventDefault(); add(Array.from(e.dataTransfer.files)); }}>
    {!!images.length && <div className="mb-2 flex flex-wrap gap-2">{images.map((src, i) => <div key={i} className="relative">
      <img src={src} alt={`Attached screenshot ${i + 1}`} className="h-16 w-20 rounded border border-border object-contain" />
      <button type="button" disabled={locked} aria-label={`Remove screenshot ${i + 1}`} onClick={() => onImagesChange(images.filter((_, n) => n !== i))} className="absolute -right-1 -top-1 rounded-full bg-background p-1"><X size={14} /></button>
    </div>)}</div>}
    <div className="flex items-end gap-2">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden" data-testid={`${testId}-files`} onChange={(e) => { add(Array.from(e.target.files)); e.target.value = ''; }} />
      <button type="button" disabled={locked || images.length >= 3} aria-label="Attach screenshots" title="Attach screenshots" className="p-2 disabled:opacity-40" onClick={() => fileRef.current.click()}><Paperclip size={18} /></button>
      <textarea ref={inputRef} rows={2} value={value} maxLength={4000} onChange={(e) => onChange(e.target.value)} disabled={locked}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) submit(e); }}
        onPaste={(e) => { const files = Array.from(e.clipboardData.items).filter((item) => item.kind === 'file').map((item) => item.getAsFile()).filter(Boolean); if (files.length) { e.preventDefault(); add(files); } }}
        placeholder={disabled ? 'Assistant unavailable' : 'Ask about your slot…'} aria-label="Message" data-testid={testId}
        className="max-h-40 min-w-0 flex-1 resize-y rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60" />
      <button type="submit" aria-label="Send" data-testid={testId === 'assistant-input' ? 'assistant-send' : `${testId}-send`} disabled={locked || (!value.trim() && !images.length)} className="rounded-xl bg-primary p-3 text-primary-foreground disabled:opacity-40"><Send size={18} /></button>
    </div>
    <div className="mt-1 text-xs text-muted-foreground">{processing ? 'Preparing screenshots…' : 'Enter to send · Shift+Enter for a new line · Paste or attach screenshots'}</div>
    {error && <div role="alert" className="mt-1 text-xs text-destructive">{error}</div>}
  </form>;
}
